import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin, Clock, MapPinHouse } from 'lucide-react';

const ContactPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
    message: '',
    agreeToTerms: false
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.agreeToTerms) {
      alert('Please agree to the terms & conditions');
      return;
    }
    console.log('Form submitted:', formData);
    alert('Thank you for contacting us! We will get back to you soon.');
    // Reset form
    setFormData({
      fullName: '',
      mobileNumber: '',
      email: '',
      message: '',
      agreeToTerms: false
    });
  };

  const contactInfo = [
    {
      icon: MapPinHouse,
      title: 'Training',
      details: 'We offer the best training for traders.',
      subDetails: 'Mon-Fri: 9AM - 6PM IST'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: 'support@spreadex.asia',
      subDetails: 'Our support team is available 24/7'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'Mumbai, Maharashtra',
      subDetails: 'India'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Monday - Friday',
      subDetails: '9:00 AM - 6:00 PM IST'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero Section */}
        <section className={`max-w-7xl mx-auto px-6 py-12 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in-down">
            Let's Get <span className="text-yellow-500">Connected</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto animate-fade-in-up animate-delay-200">
            Please fill the below form.
          </p>
        </section>

        {/* Contact Info Cards */}
        <section className="max-w-7xl mx-auto px-6 mb-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 text-center border border-gray-800 hover:border-yellow-500 transition-all duration-300 hover-lift hover-glow animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-float">
                  <info.icon className="w-8 h-8 text-yellow-500" />
                </div>
                <h3 className="text-lg font-bold mb-2">{info.title}</h3>
                <p className="text-gray-300 text-sm">{info.details}</p>
                <p className="text-gray-500 text-sm">{info.subDetails}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="max-w-4xl mx-auto px-6 animate-fade-in-up animate-delay-400">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 md:p-12 border border-gray-800 hover-glow">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name and Mobile Number Row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div>
                  <label htmlFor="fullName" className="block text-lg font-semibold mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black border-2 border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Mobile Number */}
                <div>
                  <label htmlFor="mobileNumber" className="block text-lg font-semibold mb-2">
                    Mobile Number
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-4 py-3 bg-gray-800 border-2 border-r-0 border-gray-700 rounded-l-lg text-gray-400">
                      +91
                    </span>
                    <input
                      type="tel"
                      id="mobileNumber"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      className="flex-1 px-4 py-3 bg-black border-2 border-gray-700 rounded-r-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                      placeholder="Enter 10 digit number"
                    />
                  </div>
                </div>
              </div>

              {/* E-mail */}
              <div>
                <label htmlFor="email" className="block text-lg font-semibold mb-2">
                  E-mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border-2 border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                  placeholder="example@example.com"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-lg font-semibold mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 bg-black border-2 border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors resize-none"
                  placeholder="Enter your message here..."
                ></textarea>
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  required
                  className="w-5 h-5 mt-1 bg-black border-2 border-gray-700 rounded focus:ring-2 focus:ring-yellow-500 text-yellow-500"
                />
                <label htmlFor="agreeToTerms" className="text-gray-300 text-sm leading-relaxed">
                  By clicking submit, I agree to the terms & conditions and privacy policy and give my consent to receive updates through SMS/Email.
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg py-6 rounded-xl transition-all duration-300 hover-scale animate-glow"
              >
                SUBMIT
              </Button>
            </form>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
